import {
  AiAskChart,
  AiAskItem,
  AiAskItemKind,
  AiAskResponse,
} from "../../../slices/AiSlice";
import { isInAppLink } from "./links";
import { AskProposal, normalizeProposal } from "./proposal.utils";

/*
 * "Ask ProCharted" (top search box): when to offer / pre-select the Ask row,
 * and cleaning the server's answer before it is shown.
 */

/** The Ask row shows from this many typed characters. */
export const MIN_ASK_CHARS = 3;
/** The server refuses longer questions. */
export const MAX_ASK_CHARS = 500;

export const ASK_TIP =
  "You can also ask ProCharted a question, e.g. “Which clients have unbilled hours?”";

const QUESTION_STARTS = [
  "who",
  "whose",
  "what",
  "which",
  "when",
  "where",
  "how",
  "why",
  "show",
  "list",
  "find",
  "count",
  "is",
  "are",
  "do",
  "does",
  "did",
  "give",
  "tell",
  // Requests to prepare something (Ask ProCharted actions).
  "create",
  "add",
  "make",
  "start",
];

// "new" only before a thing to create, so "New Horizons Ltd" stays a search.
const NEW_THING =
  /^new (work|client|task|job|return|organi[sz]ation|individual|contact|customer|t1|t2|t3|engagement|bookkeeping|payroll|item)\b/;

const QUESTION_PHRASES = [
  "overdue",
  "unbilled",
  "unpaid",
  "this week",
  "next week",
  "last week",
  "this month",
  "last month",
  "how many",
  "due today",
];

export const canAsk = (text: string): boolean =>
  text.trim().length >= MIN_ASK_CHARS;

/**
 * True when the typed text reads like a question rather than a name, so the
 * Ask row is pre-selected (Enter asks).
 */
export const looksLikeQuestion = (text: string): boolean => {
  const value = text.trim().toLowerCase().replace(/\s+/g, " ");
  if (!canAsk(value)) return false;
  if (value.endsWith("?")) return true;
  const firstWord = value.split(" ")[0].replace(/[^a-z']/g, "");
  if (QUESTION_STARTS.includes(firstWord) && value.includes(" ")) return true;
  if (/^set up /.test(value) || NEW_THING.test(value)) return true;
  return QUESTION_PHRASES.some((phrase) =>
    new RegExp(`(^|[^a-z])${phrase}([^a-z]|$)`).test(value),
  );
};

export { isInAppLink };

const KINDS: AiAskItemKind[] = ["work", "client", "invoice", "person"];

export type AskItem = Omit<AiAskItem, "link"> & {
  /** In-app address, or null when the server's link isn't safe to open. */
  link: string | null;
};

export type AskAnswer = {
  answer: string;
  items: AskItem[];
  moreCount: number;
  openLink: { label: string; url: string } | null;
  /** Something the AI prepared to create (only created from its window). */
  proposal: AskProposal | null;
  /** Chart of the answer's numbers (AI reporting, 2026-10-25). */
  chart?: AiAskChart | null;
};

const CHART_KINDS: AiAskChart["kind"][] = ["bar", "stacked_bar", "line"];
const CHART_UNITS: AiAskChart["unit"][] = [
  "hours",
  "money",
  "count",
  "days",
  "percent",
];

/** Keeps a chart only when its shape is complete (2-24 points, 1-4 series, numbers). */
export const normalizeChart = (raw: unknown): AiAskChart | null => {
  if (!raw || typeof raw !== "object") return null;
  const c = raw as Partial<AiAskChart>;
  if (!CHART_KINDS.includes(c.kind as AiAskChart["kind"])) return null;
  const series = (Array.isArray(c.series) ? c.series : [])
    .map((s) =>
      String(s ?? "")
        .trim()
        .slice(0, 40),
    )
    .slice(0, 4);
  if (!series.length) return null;
  const points = (Array.isArray(c.points) ? c.points : []).slice(0, 24);
  if (points.length < 2) return null;
  const ok = points.every(
    (p) =>
      p &&
      Array.isArray(p.values) &&
      p.values.length === series.length &&
      p.values.every((v) => typeof v === "number" && Number.isFinite(v)),
  );
  if (!ok) return null;
  return {
    kind: c.kind as AiAskChart["kind"],
    title: String(c.title ?? "")
      .trim()
      .slice(0, 80),
    unit: CHART_UNITS.includes(c.unit as AiAskChart["unit"])
      ? (c.unit as AiAskChart["unit"])
      : "count",
    series,
    points: points.map((p) => ({
      label: String(p.label ?? "")
        .trim()
        .slice(0, 60),
      values: p.values,
    })),
  };
};

/** Cleans the server's answer: known kinds, in-app links only, no blanks. */
export const normalizeAskResponse = (
  response?: Partial<AiAskResponse> | null,
): AskAnswer => {
  const items: AskItem[] = (
    Array.isArray(response?.items) ? response?.items || [] : []
  )
    .filter(
      (item): item is AiAskItem =>
        !!item && typeof item.title === "string" && !!item.title.trim(),
    )
    .map((item) => ({
      kind: KINDS.includes(item.kind) ? item.kind : "work",
      id: String(item.id ?? ""),
      title: item.title.trim(),
      subtitle:
        typeof item.subtitle === "string" && item.subtitle.trim()
          ? item.subtitle.trim()
          : undefined,
      link: isInAppLink(item.link) ? item.link.trim() : null,
    }));
  const more = Number(response?.moreCount);
  const openLink =
    response?.openLink &&
    isInAppLink(response.openLink.url) &&
    typeof response.openLink.label === "string" &&
    response.openLink.label.trim()
      ? {
          label: response.openLink.label.trim(),
          url: response.openLink.url.trim(),
        }
      : null;
  return {
    answer: typeof response?.answer === "string" ? response.answer.trim() : "",
    items,
    moreCount: Number.isFinite(more) && more > 0 ? Math.floor(more) : 0,
    openLink,
    proposal: normalizeProposal(response?.proposal),
    chart: normalizeChart(response?.chart),
  };
};

export const moreText = (count: number): string =>
  count > 0 ? `+${count} more` : "";

export const ASK_HISTORY_TURNS = 5;

/**
 * Earlier turns of the open Ask window, sent with a follow-up question
 * (Roadmap 1834): newest 5 answered turns, the answer text plus the listed
 * items' titles so "the second one" or "those clients" make sense.
 */
export const askHistory = (
  turns: { question: string; answer?: AskAnswer | null }[],
): { question: string; answer: string }[] =>
  turns
    .filter((turn) => !!turn.answer)
    .slice(-ASK_HISTORY_TURNS)
    .map(({ question, answer }) => {
      const items = (answer?.items || [])
        .map(
          (item, index) =>
            `${index + 1}. ${item.title}${
              item.subtitle ? ` (${item.subtitle})` : ""
            }`,
        )
        .join("; ");
      return {
        question,
        answer: [answer?.answer || "", items && `Listed: ${items}`]
          .filter(Boolean)
          .join("\n"),
      };
    });
